import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, extractErrorMessage } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/Toast';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Confirm } from '../components/Confirm';
import { Modal } from '../components/Modal';
import { Pagination } from '../components/Pagination';
import { downloadCsv, rowsToCsv } from '../hooks/useCsvExport';
import { useXlsxExport } from '../hooks/useXlsxExport';
import { SWITCH as SWITCH_SKU_ML } from './SkuEnMercadoLibre';
import { VistaPreviaSyncSku } from './VistaPreviaSyncSku';
import { PacksSinSincronizar } from './PacksSinSincronizar';
import { CombosSinSincronizar } from './CombosSinSincronizar';
import { PublicadosSinComposicion } from './PublicadosSinComposicion';

/**
 * "Sincronizar con ML": el camino planilla 02_CODIGOS → ERP → Mercado Libre en una sola pantalla. Una tarjeta de estado
 * (qué SKU van a ML; la planilla se trae desde Configuración), el aviso si "Escribir el SKU en Mercado Libre" se apagó solo (el admin va a Configuración
 * a revisarlo), el aviso de cuántas publicaciones no se actualizan en Mercado Libre y por qué (con un botón a cada lista), y
 * la lista del catálogo con filtros para sincronizar o detener de a muchos SKU en un lote que corre en el servidor.
 * Sincronizar sólo admin; detener también con catalogo:editar. «Ver» abre la vista previa de un SKU (VistaPreviaSyncSku);
 * abajo, a demanda, «Combos sin sincronizar», «Packs sin sincronizar» y «Publicados sin composición». Los interruptores de
 * escritura y el panel «SKU en Mercado Libre» están en Configuración → Mercado Libre y catálogo.
 */

const URL_BASE = '/api/v3/ml/sync-sku';
const POR_PAGINA = 50;
/** Mientras corre un lote, se lee su progreso cada tanto. */
const SONDEO_MS = 2000;
/** Tope de SKU de un lote (MAX_SKUS_LOTE_SYNC_SKU en el backend). */
const MAX_LOTE = 2000;
/** Lecturas seguidas del progreso que fallan antes de dejar de consultar. */
const MAX_FALLAS_SONDEO = 3;
const MENSAJE_INTERRUMPIDO = 'El lote se interrumpió (se reinició el servidor). Volvé a lanzarlo: los SKU que ya sincronizan se saltean.';
const MENSAJE_SIN_PROGRESO = 'No se pudo leer el progreso del lote: se dejó de consultar. El lote sigue en el servidor; volvé a abrir la pantalla para ver cómo va.';

interface Fila {
  skuInterno: string;
  descripcion: string | null;
  marca: string | null;
  proveedorNombre: string | null;
  disponible: number;
  contado: boolean;
  sincronizando: boolean;
  ultimaSyncEn: string | null;
  ultimaSyncOk: boolean | null;
  ultimaSyncResultado: string | null;
  /** Combo o pack: sin stock propio (disponible 0); `combosDisponibles`: cuántos se pueden armar con sus componentes (null: sin calcular). */
  esCombo?: boolean;
  combosDisponibles?: number | null;
  /** Combos/packs: lo armable menos el stock de resguardo de cada componente. */
  sePublica?: number | null;
}
interface Pagina {
  total: number; sinConteo: number; pagina: number; porPagina: number; filas: Fila[];
  /** Cuándo se calcularon los combos disponibles (el backend los recalcula cada 5 minutos); null si todavía no. */
  combosCalculadoEn?: string | null;
}
interface Resumen { activos: number; conError: number; ultimaSync: string | null }
/** GET publicaciones-sin-actualizar-resumen: publicaciones abiertas que hoy no se actualizan en ML, por qué, y SKU con error. */
interface ResumenSinActualizar {
  total: number;
  combosListos: { combos: number; publicaciones: number };
  combosPendientes: { combos: number; publicaciones: number };
  packs: { packs: number; publicaciones: number };
  sinComposicion: { skus: number; publicaciones: number };
  skusConErrorMl: number;
}
type ListaSinActualizar = 'combos' | 'packs' | 'sinComposicion' | 'errores';
interface EstadoSkuMl { escrituraSkuMl: boolean; apagadoSolo: { motivo: string; mlaId: string } | null; detenidoSolo?: boolean }
type Accion = 'habilitar' | 'detener';
interface EstadoLote {
  enCurso: boolean; accion: Accion | null; total: number; hechos: number; omitidos: number; conError: number;
  errores: Array<{ sku: string; error: string }>; terminadoEn: string | null;
  /** Por qué se cortó antes de terminar (varios SKU seguidos fallaron); null si no se cortó. */
  cortado?: string | null;
}
interface Filtro { q: string; marca: string; proveedor: string; estado: string; stock: string }
/** Qué confirma el diálogo del lote: la acción y los SKU elegidos (null = los que coinciden con el filtro). */
/** Un lote va por SKU elegidos o por el filtro con que se cargó la lista: sin filtro no hay lote «de todo el catálogo» por error. */
type PedidoLote = { accion: Accion; cuantos: number; sinConteo: number } & ({ skus: string[]; filtro?: undefined } | { skus: null; filtro: Filtro });

const FILTRO_VACIO: Filtro = { q: '', marca: '', proveedor: '', estado: '', stock: '' };
/** La lista arranca en «Necesita atención» (error, o contados con una publicación abierta que no sincronizan); «Todos» es el filtro vacío. */
const AYUDA_FILTRO_ESTADO = '«Necesita atención»: con error, o contados y con una publicación abierta que todavía no sincronizan';
const FILTRO_INICIAL: Filtro = { ...FILTRO_VACIO, estado: 'atencion' };
const ESTADOS = [['atencion', 'Necesita atención'], ['', 'Todos'], ['sincronizando', 'Sincronizando'], ['no', 'No sincroniza'], ['error', 'Con error']] as const;
const STOCKS = [['', 'Todos'], ['con', 'Con stock'], ['sin', 'Sin stock'], ['sin_conteo', 'Sin conteo']] as const;

const n = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('es-AR'));
const rtf = new Intl.RelativeTimeFormat('es-AR', { numeric: 'auto' });
/** "hace 5 minutos", "ayer"… */
function hace(iso: string | null | undefined): string {
  if (!iso) return '—';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  const [v, u]: [number, Intl.RelativeTimeFormatUnit] = s < 60 ? [s, 'second'] : s < 3600 ? [s / 60, 'minute'] : s < 86400 ? [s / 3600, 'hour'] : [s / 86400, 'day'];
  return rtf.format(-Math.round(v), u);
}
const filtroDeQuery = (f: Filtro) => Object.fromEntries(Object.entries(f).filter(([, v]) => v !== '')) as Partial<Filtro>;

/** Tope de filas del export (MAX_FILAS_EXPORTAR_CANDIDATOS en el backend). */
const MAX_EXPORTAR = 30000;
const COLUMNAS_EXPORTAR = [
  { key: 'skuInterno', header: 'SKU' },
  { key: 'descripcion', header: 'Descripción' },
  { key: 'marca', header: 'Marca' },
  { key: 'proveedorNombre', header: 'Proveedor' },
  { key: 'disponible', header: 'Disponible' },
  { key: 'combosDisponibles', header: 'Combos disponibles' },
  { key: 'sePublica', header: 'Se publica' },
  { key: 'contado', header: 'Contado' },
  { key: 'estadoMl', header: 'Estado ML' },
  { key: 'ultimaSync', header: 'Última sync' },
  { key: 'ultimaSyncResultado', header: 'Resultado' },
];
/** Los combos y packs no tienen stock propio: el filtro de stock los cuenta por lo armable (backend: tabla combo_disponible). */
const AYUDA_FILTRO_STOCK = 'Con stock / sin stock: los combos y packs cuentan por lo que se puede armar con sus componentes (su columna «Combos disponibles»; se recalcula cada 5 minutos).';

const estadoMl = (f: Fila) => (f.sincronizando && f.ultimaSyncOk === false ? 'Con error' : f.sincronizando ? 'Sincronizando' : 'No sincroniza');
function EstadoMl({ f }: { f: Fila }) {
  const e = estadoMl(f);
  return <Badge tone={e === 'Con error' ? 'red' : e === 'Sincronizando' ? 'green' : 'slate'}>{e}</Badge>;
}

/**
 * Panel cerrado que monta su contenido (y carga sus datos) recién la primera vez que se abre. Después queda montado aunque
 * se cierre, así un envío en curso no se corta. `abrir`: al cambiar (un contador), lo abre y lo lleva a la vista.
 */
export function PanelDesplegable({ titulo, abrir, children }: { titulo: string; abrir?: number; children: ReactNode }) {
  const [montado, setMontado] = useState(false);
  const ref = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    if (!abrir || !ref.current) return;
    ref.current.open = true;
    setMontado(true);
    ref.current.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [abrir]);
  return (
    <details ref={ref} className="rounded-lg border border-slate-200 bg-white" onToggle={(e) => { if (e.currentTarget.open) setMontado(true); }}>
      <summary className="cursor-pointer select-none px-4 py-2 text-base font-semibold text-slate-800">{titulo}</summary>
      {montado && <div className="px-4 pb-4">{children}</div>}
    </details>
  );
}

const publicaciones = (k: number) => `${n(k)} ${k === 1 ? 'publicación' : 'publicaciones'}`;

/** Aviso de publicaciones que no se actualizan en Mercado Libre (2026-11-02), en lenguaje simple y con un botón a cada lista. */
function AvisoSinActualizar({ r, onVer }: { r: ResumenSinActualizar | null; onVer: (lista: ListaSinActualizar) => void }) {
  if (!r || (r.total === 0 && r.skusConErrorMl === 0)) return null;
  const { combosListos: listos, combosPendientes: pendientes, packs, sinComposicion: sinComp } = r;
  const lineas = [
    listos.publicaciones > 0 && `${publicaciones(listos.publicaciones)} de ${n(listos.combos)} ${listos.combos === 1 ? 'combo listo' : 'combos listos'} para sincronizar`,
    pendientes.publicaciones > 0 && `${publicaciones(pendientes.publicaciones)} de ${n(pendientes.combos)} ${pendientes.combos === 1 ? 'combo al que le falta' : 'combos a los que les falta'} sincronizar o contar un componente`,
    packs.publicaciones > 0 && `${publicaciones(packs.publicaciones)} de ${n(packs.packs)} ${packs.packs === 1 ? 'pack' : 'packs'} sin sincronizar`,
    sinComp.publicaciones > 0 && `${publicaciones(sinComp.publicaciones)} de ${n(sinComp.skus)} SKU sin composición (el ERP no sabe de qué ${sinComp.skus === 1 ? 'está hecho' : 'están hechos'})`,
    r.skusConErrorMl > 0 && (r.skusConErrorMl === 1
      ? '1 SKU que sincroniza dio error en Mercado Libre en su última sincronización (por ejemplo, una publicación en revisión)'
      : `${n(r.skusConErrorMl)} SKU que sincronizan dieron error en Mercado Libre en su última sincronización (por ejemplo, una publicación en revisión)`),
  ].filter((l): l is string => !!l);
  const botones: Array<[ListaSinActualizar, string, boolean]> = [
    ['combos', 'Ver combos sin sincronizar', listos.publicaciones + pendientes.publicaciones > 0],
    ['packs', 'Ver packs sin sincronizar', packs.publicaciones > 0],
    ['sinComposicion', 'Ver publicados sin composición', sinComp.publicaciones > 0],
    ['errores', 'Ver los SKU con error', r.skusConErrorMl > 0],
  ];
  return (
    <section aria-label="Publicaciones que no se actualizan en Mercado Libre" className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 space-y-1">
      {r.total > 0 && (
        <p className="font-medium">{`${publicaciones(r.total)} ${r.total === 1 ? 'no se está actualizando' : 'no se están actualizando'} en Mercado Libre: se quedan con el stock viejo.`}</p>
      )}
      <ul className="list-disc pl-5">{lineas.map((l) => <li key={l}>{l}</li>)}</ul>
      <div className="flex flex-wrap gap-2">
        {botones.filter(([, , ver]) => ver).map(([lista, texto]) => (
          <Button key={lista} size="sm" variant="secondary" onClick={() => onVer(lista)}>{texto}</Button>
        ))}
      </div>
    </section>
  );
}

export function SincronizarConMl({ debajoDelAviso }: { debajoDelAviso?: ReactNode } = {}) {
  const { user, can } = useAuth();
  const { addToast } = useToast();
  const esAdmin = user?.role === 'admin';
  // Mismos permisos que el backend: sincronizar sólo admin; detener también con catalogo:editar.
  const puedeDetener = esAdmin || can('catalogo', 'editar');

  const [resumen, setResumen] = useState<Resumen | null>(null);
  const [sinActualizar, setSinActualizar] = useState<ResumenSinActualizar | null>(null);
  /** Botones del aviso: cada clic abre su panel de abajo. */
  const [abrirLista, setAbrirLista] = useState<Record<Exclude<ListaSinActualizar, 'errores'>, number>>({ combos: 0, packs: 0, sinComposicion: 0 });
  const [skuMl, setSkuMl] = useState<EstadoSkuMl | null>(null);
  const [opciones, setOpciones] = useState<{ marcas: string[]; proveedores: string[] }>({ marcas: [], proveedores: [] });
  const [texto, setTexto] = useState('');
  const [filtro, setFiltro] = useState<Filtro>(FILTRO_INICIAL);
  const [pagina, setPagina] = useState(1);
  /** La página cargada y el filtro con el que se pidió (lo que confirma y manda «los N que coinciden»). */
  const [datos, setDatos] = useState<(Pagina & { filtro: Filtro }) | null>(null);
  const [cargando, setCargando] = useState(false);
  // Si la lista no se pudo cargar no se dice «Ningún SKU…»: sería un falso «todo bien».
  const [falloLista, setFalloLista] = useState(false);
  /** Elegidos (de cualquier página), con su fila para saber si tienen conteo. */
  const [seleccion, setSeleccion] = useState<Map<string, Fila>>(new Map());
  const [pedido, setPedido] = useState<PedidoLote | null>(null);
  const [lote, setLote] = useState<EstadoLote | null>(null);
  /** Reemplaza la línea de progreso cuando el sondeo se interrumpe (servidor reiniciado o sin respuesta). */
  const [avisoLote, setAvisoLote] = useState<string | null>(null);
  const [verSku, setVerSku] = useState<string | null>(null);
  const [exportando, setExportando] = useState(false);
  const { exportXlsx } = useXlsxExport();

  /** Número del último pedido de la lista: la respuesta de uno anterior se descarta. */
  const ultimoPedido = useRef(0);
  const cargar = useCallback(async () => {
    const nro = ++ultimoPedido.current;
    setCargando(true);
    try {
      const params = new URLSearchParams({ ...filtroDeQuery(filtro), pagina: String(pagina), porPagina: String(POR_PAGINA) });
      const r = await api.get<Pagina>(`${URL_BASE}/candidatos?${params}`);
      if (nro !== ultimoPedido.current) return;
      // La lista se achicó y la página quedó después de la última: se pide la última.
      const ultima = Math.max(1, Math.ceil(r.total / r.porPagina));
      if (r.total > 0 && pagina > ultima) { setPagina(ultima); return; }
      setDatos({ ...r, filtro });
      setFalloLista(false);
    } catch (e) {
      if (nro === ultimoPedido.current) {
        setFalloLista(true);
        addToast(extractErrorMessage(e), 'error');
      }
    } finally {
      if (nro === ultimoPedido.current) setCargando(false);
    }
  }, [addToast, filtro, pagina]);
  useEffect(() => { void cargar(); }, [cargar]);

  const cargarResumen = useCallback(() => {
    api.get<Resumen>(`${URL_BASE}/resumen`).then(setResumen).catch(() => {});
  }, []);
  const cargarSkuMl = useCallback(() => {
    api.get<EstadoSkuMl>('/api/v3/ml/sku-ml?porPagina=1').then(setSkuMl).catch(() => {});
  }, []);
  useEffect(() => {
    cargarResumen();
    cargarSkuMl();
    api.get<{ marcas: string[]; proveedores: string[] }>(`${URL_BASE}/opciones-filtro`).then(setOpciones).catch(() => {});
    api.get<ResumenSinActualizar>(`${URL_BASE}/publicaciones-sin-actualizar-resumen`).then(setSinActualizar).catch(() => {});
    // Un lote que ya corría (lanzado antes o desde otra computadora): se sigue su progreso.
    api.get<EstadoLote>(`${URL_BASE}/lote/estado`).then((e) => { if (e?.enCurso) setLote(e); }).catch(() => {});
  }, [cargarResumen, cargarSkuMl]);

  const enCurso = lote?.enCurso === true;
  useEffect(() => {
    if (!enCurso) return undefined;
    let fallas = 0;
    const t = setInterval(async () => {
      try {
        const e = await api.get<EstadoLote>(`${URL_BASE}/lote/estado`);
        fallas = 0;
        // El lote vive en memoria del servidor: si se reinició, vuelve el estado vacío (total 0).
        if (!e.enCurso && e.total === 0) {
          setLote((l) => l && { ...l, enCurso: false });
          setAvisoLote(MENSAJE_INTERRUMPIDO);
        } else setLote(e);
        if (!e.enCurso) {
          setSeleccion(new Map());
          void cargar();
          cargarResumen();
        }
      } catch {
        // Se reintenta en la próxima vuelta; después de varias seguidas, se deja de consultar.
        if (++fallas >= MAX_FALLAS_SONDEO) {
          setLote((l) => l && { ...l, enCurso: false });
          setAvisoLote(MENSAJE_SIN_PROGRESO);
        }
      }
    }, SONDEO_MS);
    return () => clearInterval(t);
  }, [enCurso, cargar, cargarResumen]);

  const cambiarFiltro = (k: keyof Filtro, v: string) => { setPagina(1); setFiltro((f) => ({ ...f, [k]: v })); };
  const buscar = (e: FormEvent) => { e.preventDefault(); cambiarFiltro('q', texto.trim()); };
  const verLista = (lista: ListaSinActualizar) => {
    if (lista === 'errores') cambiarFiltro('estado', 'error');
    else setAbrirLista((a) => ({ ...a, [lista]: a[lista] + 1 }));
  };

  const lanzarLote = async () => {
    const p = pedido;
    setPedido(null);
    if (!p) return;
    try {
      const body = p.skus ? { accion: p.accion, skus: p.skus } : { accion: p.accion, filtro: filtroDeQuery(p.filtro) };
      const r = await api.post<{ total: number }>(`${URL_BASE}/lote`, body);
      setAvisoLote(null);
      setLote({ enCurso: true, accion: p.accion, total: r.total, hechos: 0, omitidos: 0, conError: 0, errores: [], terminadoEn: null });
    } catch (e) {
      addToast(extractErrorMessage(e), 'error');
    }
  };

  /** Todos los que coinciden con el filtro (no sólo la página), con las columnas de la lista. */
  const exportar = async (formato: 'csv' | 'xlsx') => {
    setExportando(true);
    try {
      const r = await api.get<{ truncado: boolean; filas: Fila[] }>(`${URL_BASE}/candidatos/exportar?${new URLSearchParams(filtroDeQuery(filtro))}`);
      if (r.truncado) addToast(`Coinciden más de ${n(MAX_EXPORTAR)} SKU: el archivo tiene los primeros ${n(MAX_EXPORTAR)}. Achicá el filtro para el resto.`, 'error');
      const filasExport = r.filas.map((f) => ({
        ...f,
        contado: f.contado ? 'Sí' : 'No',
        estadoMl: estadoMl(f),
        ultimaSync: f.ultimaSyncEn ? new Date(f.ultimaSyncEn).toLocaleString('es-AR') : '',
      }));
      if (formato === 'xlsx') await exportXlsx(COLUMNAS_EXPORTAR, filasExport, 'Sincronizar-con-ML');
      else downloadCsv(rowsToCsv(COLUMNAS_EXPORTAR, filasExport), `Sincronizar-con-ML-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (e) {
      addToast(extractErrorMessage(e), 'error');
    } finally {
      setExportando(false);
    }
  };

  const filas = datos?.filas ?? [];
  const total = datos?.total ?? 0;
  const combosCalculados = datos?.combosCalculadoEn ? `Combos calculados ${hace(datos.combosCalculadoEn)}` : null;
  const elegidos = [...seleccion.values()];
  const todosEnPagina = filas.length > 0 && filas.every((f) => seleccion.has(f.skuInterno));
  const alternar = (f: Fila) => setSeleccion((s) => {
    const m = new Map(s);
    if (m.has(f.skuInterno)) m.delete(f.skuInterno); else m.set(f.skuInterno, f);
    return m;
  });
  const alternarPagina = () => setSeleccion((s) => {
    const m = new Map(s);
    for (const f of filas) { if (todosEnPagina) m.delete(f.skuInterno); else m.set(f.skuInterno, f); }
    return m;
  });
  const pedirSeleccion = (accion: Accion) =>
    setPedido({ accion, skus: elegidos.map((f) => f.skuInterno), cuantos: elegidos.length, sinConteo: elegidos.filter((f) => !f.contado).length });
  const pedirCoinciden = (accion: Accion) => {
    if (datos) setPedido({ accion, skus: null, cuantos: datos.total, sinConteo: datos.sinConteo, filtro: datos.filtro });
  };
  // La cuenta de «los N que coinciden» es la de la página cargada: mientras llega la del filtro nuevo, no se ofrece.
  const desactualizado = cargando || datos?.filtro !== filtro;
  const demasiados = total > MAX_LOTE;
  const tituloCoinciden = demasiados ? `Coinciden más de ${n(MAX_LOTE)}: achicá el filtro` : undefined;

  const apagadoSolo = skuMl?.apagadoSolo || skuMl?.detenidoSolo === true;

  return (
    <div className="space-y-4">
      {apagadoSolo && (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-800">
          <span>
            {skuMl?.apagadoSolo
              ? `«${SWITCH_SKU_ML}» se apagó solo porque ${skuMl.apagadoSolo.motivo}. Revisá la publicación ${skuMl.apagadoSolo.mlaId} en Mercado Libre antes de volver a prender.`
              : `«${SWITCH_SKU_ML}» se apagó solo por un cambio inesperado en una publicación. Revisá en Mercado Libre antes de volver a prender.`}
          </span>
          {esAdmin && <Link to="/configuracion-mercado-libre" className="text-sm font-semibold text-red-900 underline">Revisar en Configuración</Link>}
        </div>
      )}

      <AvisoSinActualizar r={sinActualizar} onVer={verLista} />

      {debajoDelAviso}

      <section aria-label="Qué va a ML" className="rounded-lg border border-slate-200 bg-white px-4 py-2">
        <p className="text-sm text-slate-700">
          {resumen ? `${n(resumen.activos)} SKU sincronizando` : '…'}
          {resumen && resumen.conError > 0 && <Badge tone="red" className="ml-2">{`${n(resumen.conError)} con error`}</Badge>}
          {resumen?.ultimaSync && <span className="text-slate-500">{` · Última sincronización: ${hace(resumen.ultimaSync)}`}</span>}
        </p>
      </section>

      {lote && (
        <section aria-label="Progreso del lote" className="rounded-lg border border-slate-200 bg-white p-4 space-y-2">
          <p className="text-sm font-medium text-slate-800">
            {avisoLote ?? `${lote.enCurso ? (lote.accion === 'detener' ? 'Dejando de sincronizar' : 'Sincronizando') : 'Terminó'}: ${n(lote.hechos)} de ${n(lote.total)}`}
            {lote.omitidos > 0 && ` · ${n(lote.omitidos)} ya estaban`}
            {lote.conError > 0 && <span className="text-red-700">{` · ${n(lote.conError)} con error`}</span>}
          </p>
          {lote.cortado && <p className="text-sm text-red-700">{lote.cortado}</p>}
          <div role="progressbar" aria-label="Progreso del lote" aria-valuemin={0} aria-valuemax={lote.total} aria-valuenow={lote.hechos}
            className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-brand-600 transition-all" style={{ width: `${lote.total ? (100 * lote.hechos) / lote.total : 0}%` }} />
          </div>
          {lote.errores.length > 0 && (
            <ul className="text-xs text-red-700 space-y-0.5 max-h-32 overflow-y-auto">
              {lote.errores.map((e) => <li key={e.sku}>{`${e.sku}: ${e.error}`}</li>)}
            </ul>
          )}
        </section>
      )}

      <section aria-label="SKU del catálogo" className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <form onSubmit={buscar} className="flex items-end gap-2">
            <input type="search" aria-label="Buscar por SKU o descripción" placeholder="SKU o descripción"
              className="w-full sm:w-56 rounded border border-slate-300 px-2 py-1 text-sm" value={texto} onChange={(e) => setTexto(e.target.value)} />
            <Button type="submit" variant="secondary" size="sm">Buscar</Button>
          </form>
          {([
            ['marca', 'Marca', [['', 'Todas'], ...opciones.marcas.map((m) => [m, m])]],
            ['proveedor', 'Proveedor', [['', 'Todos'], ...opciones.proveedores.map((p) => [p, p])]],
            ['estado', 'Estado', ESTADOS],
            ['stock', 'Stock', STOCKS],
          ] as Array<[keyof Filtro, string, ReadonlyArray<readonly [string, string]>]>).map(([k, etiqueta, valores]) => (
            <label key={k} className="text-xs text-slate-600">
              {etiqueta}
              <select aria-label={etiqueta} value={filtro[k]} onChange={(e) => cambiarFiltro(k, e.target.value)} title={k === 'stock' ? AYUDA_FILTRO_STOCK : k === 'estado' ? AYUDA_FILTRO_ESTADO : undefined}
                className="block max-w-[12rem] rounded border border-slate-300 px-2 py-1 text-sm">
                {valores.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
              </select>
            </label>
          ))}
          <Button variant="ghost" size="sm" onClick={() => void cargar()} loading={cargando}>Actualizar</Button>
          <Button variant="secondary" size="sm" onClick={() => void exportar('csv')} disabled={exportando || total === 0}
            title="Todos los SKU que coinciden con el filtro, no sólo esta página">Exportar CSV</Button>
          <Button variant="secondary" size="sm" onClick={() => void exportar('xlsx')} disabled={exportando || total === 0}
            title="Todos los SKU que coinciden con el filtro, no sólo esta página">Exportar XLSX</Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {esAdmin && (
            <>
              <Button size="sm" onClick={() => pedirSeleccion('habilitar')} disabled={enCurso || elegidos.length === 0}>{`Sincronizar seleccionados (${n(elegidos.length)})`}</Button>
              <Button size="sm" variant="secondary" onClick={() => pedirCoinciden('habilitar')} disabled={enCurso || desactualizado || total === 0 || demasiados} title={tituloCoinciden}>{`Sincronizar los ${n(total)} que coinciden`}</Button>
            </>
          )}
          {puedeDetener && (
            <>
              <Button size="sm" variant="ghost" onClick={() => pedirSeleccion('detener')} disabled={enCurso || elegidos.length === 0}>{`Dejar de sincronizar seleccionados (${n(elegidos.length)})`}</Button>
              <Button size="sm" variant="ghost" onClick={() => pedirCoinciden('detener')} disabled={enCurso || desactualizado || total === 0 || demasiados} title={tituloCoinciden}>{`Dejar de sincronizar los ${n(total)} que coinciden`}</Button>
            </>
          )}
          {elegidos.length > 0 && <Button size="sm" variant="ghost" onClick={() => setSeleccion(new Map())}>Limpiar selección</Button>}
        </div>

        {combosCalculados && <p className="text-xs text-slate-500">{`${combosCalculados} (se recalculan cada 5 minutos)`}</p>}
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200">
                <th className="py-1 px-2"><input type="checkbox" aria-label="Elegir todos los de esta página" checked={todosEnPagina} onChange={alternarPagina} /></th>
                <th className="py-1 px-2 font-medium">SKU</th>
                <th className="py-1 px-2 font-medium">Descripción</th>
                <th className="py-1 px-2 font-medium">Marca</th>
                <th className="py-1 px-2 font-medium">Proveedor</th>
                <th className="py-1 px-2 font-medium text-right">Stock</th>
                <th className="py-1 px-2 font-medium text-right" title={`Combos y packs: cuántos se pueden armar con el stock de sus componentes${combosCalculados ? `. ${combosCalculados}` : ''}`}>Combos disponibles</th>
                <th className="py-1 px-2 font-medium">Estado ML</th>
                <th className="py-1 px-2 font-medium">Última sync</th>
                <th className="py-1 px-2"><span className="sr-only">Ver</span></th>
              </tr>
            </thead>
            <tbody>
              {filas.length === 0 && (
                <tr><td colSpan={10} className="py-3 px-2 text-slate-500">{cargando ? 'Cargando…' : falloLista ? 'No se pudo cargar la lista de SKU. Probá de nuevo en un rato.' : filtro.estado === 'atencion' ? 'Ningún SKU necesita atención. Para ver el catálogo entero, elegí «Todos» en Estado.' : 'Ningún SKU coincide con el filtro'}</td></tr>
              )}
              {filas.map((f) => (
                <tr key={f.skuInterno} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-1 px-2"><input type="checkbox" aria-label={`Elegir ${f.skuInterno}`} checked={seleccion.has(f.skuInterno)} onChange={() => alternar(f)} /></td>
                  <td className="py-1 px-2 font-medium">{f.skuInterno}</td>
                  <td className="py-1 px-2">{f.descripcion ?? '—'}</td>
                  <td className="py-1 px-2 text-xs">{f.marca ?? '—'}</td>
                  <td className="py-1 px-2 text-xs">{f.proveedorNombre ?? '—'}</td>
                  <td className="py-1 px-2 text-right tabular-nums">
                    {n(f.disponible)}
                    {!f.contado && <span className="block text-xs text-amber-700">sin conteo</span>}
                  </td>
                  <td className="py-1 px-2 text-right tabular-nums">
                    {f.esCombo && f.combosDisponibles != null && (
                      <>
                        {n(f.combosDisponibles)}
                        {f.sePublica != null && f.sePublica !== f.combosDisponibles && <span className="block text-xs text-slate-500">{`se publica ${n(f.sePublica)}`}</span>}
                      </>
                    )}
                  </td>
                  <td className="py-1 px-2"><EstadoMl f={f} /></td>
                  <td className="py-1 px-2 text-xs" title={f.ultimaSyncResultado ?? undefined}>{f.ultimaSyncEn ? hace(f.ultimaSyncEn) : '—'}</td>
                  <td className="py-1 px-2 text-right"><Button size="sm" variant="ghost" onClick={() => setVerSku(f.skuInterno)}>Ver</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {datos && total > datos.porPagina && <Pagination page={pagina} pageSize={datos.porPagina} total={total} onChange={setPagina} />}
      </section>

      <PanelDesplegable titulo="Combos sin sincronizar" abrir={abrirLista.combos}>
        <CombosSinSincronizar esAdmin={esAdmin} />
      </PanelDesplegable>
      <PanelDesplegable titulo="Packs sin sincronizar de SKU que ya sincronizan" abrir={abrirLista.packs}>
        <PacksSinSincronizar esAdmin={esAdmin} />
      </PanelDesplegable>
      <PanelDesplegable titulo="Publicados sin composición" abrir={abrirLista.sinComposicion}>
        <PublicadosSinComposicion />
      </PanelDesplegable>

      <Confirm
        open={pedido !== null}
        title={pedido?.accion === 'detener' ? 'Dejar de sincronizar' : 'Sincronizar con Mercado Libre'}
        message={!pedido ? '' : pedido.accion === 'habilitar'
          ? `Se van a sincronizar ${n(pedido.cuantos)} SKU, cada uno con todos sus packs (x12, x25…): el ERP escribe su stock en `
            + 'Mercado Libre en el momento, de a un SKU. Los que ya sincronizan se saltean.'
            + (pedido.sinConteo > 0
              ? `\n\n${n(pedido.sinConteo)} ${pedido.sinConteo === 1 ? 'todavía no tiene conteo' : 'todavía no tienen conteo'}: lo que se publique no sale de un stock declarado (no bloquea).`
              : '')
          : `Se van a dejar de sincronizar ${n(pedido.cuantos)} SKU y sus packs. El ERP deja de escribir su stock en Mercado Libre; `
            + 'las publicaciones quedan con el stock y el estado que tienen ahora. Los que no sincronizan se saltean.'}
        confirmLabel={pedido?.accion === 'detener' ? 'Dejar de sincronizar' : 'Sincronizar'}
        danger={pedido?.accion === 'detener'}
        onCancel={() => setPedido(null)}
        onConfirm={() => void lanzarLote()}
      />
      <Modal open={verSku !== null} onClose={() => setVerSku(null)} title={`Vista previa de ${verSku ?? ''}`} size="lg">
        {verSku && <VistaPreviaSyncSku sku={verSku} onCambio={() => { void cargar(); cargarResumen(); }} />}
      </Modal>
    </div>
  );
}
